import * as React from "react";
import { useState } from "react";
import { Link } from "../common/components/link";
import { links } from "../routes";
import { attach } from "../common/componentExtensions";
import UIStore, { ScreenSize } from "../common/UIStore";
import { useLocation } from "react-router-dom";
import SidebarUserProfile from "./sidebarUserProfile";
import { BoilerIcon } from "../common/components/icons";
import { canRequireUser, accountIsExpired } from "../login/loginHelpers";
import { AccountData } from "../generated";
import NewsRepository from "../news/NewsRepository";

interface SiderbarMenuProps {
    title: string;
    icon: string | JSX.Element;
    items: SidebarLinkItem[];
}


interface SidebarLinkItem {
    title: string;
    link: string;
    icon?: string;
}

let SidebarLink: React.FunctionComponent<SidebarLinkItem> = ({ title, link, icon }) => {
    return (<li>
        <Link to={link} onClick={() => UIStore.closeSidebarIfNecessary()}>
            <>{icon && <i className={icon} />}  {title}</>
        </Link>
    </li>);
};

let SidebarSingleMenu: React.FunctionComponent<{ title: string | JSX.Element, link: string, icon: string }> = ({ title, link, icon }) => {
    const location = useLocation();
    return (<li className={link === location.pathname ? "active" : ""}>
        <Link to={link} onClick={() => UIStore.closeSidebarIfNecessary()}>
            {icon && <i className={icon} />}  <span className="hide-menu">{title}</span>
        </Link>
    </li >);
};

export const SidebarMenu: React.FunctionComponent<SiderbarMenuProps> = ({ title, icon, items }) => {
    const location = useLocation();
    const isOpenFromLocation = items.find(x => x.link === location.pathname) != null;
    const [isOpen, setOpen] = useState(isOpenFromLocation);
    return (
        <li className={isOpen ? "active" : ""} onClick={() => setOpen(!isOpen)}>
            <a className="has-arrow waves-effect waves-dark" aria-expanded="false">
                {typeof icon === "string" ? <i className={icon} /> : icon}
                < span className="hide-menu">{title}</span>
            </a>
            <ul aria-expanded="false" className={`collapse ${isOpen ? "in" : ""}`} onClick={e => e.stopPropagation()}>
                {items.map((item, i) => {
                    return <SidebarLink key={i} title={item.title} link={item.link} />;
                })}
            </ul>
        </li >
    );
}

interface SidebarProps {
    screenType: ScreenSize;
    user: AccountData | null;
}
const Sidebar: React.FunctionComponent<SidebarProps> = ({ screenType, user }) => {
    const accountExpired = accountIsExpired(user);
    const news = NewsRepository.getForUser(user);
    return (
        <aside className="left-sidebar d-print-none" style={{ overflow: screenType <= ScreenSize.Tablet ? "visible" : undefined, overflowY: "auto" }}>
            <div className="scroll-sidebar" style={{ overflowX: screenType <= ScreenSize.Tablet ? "visible" : undefined }}>
                <SidebarUserProfile />
                <nav className="sidebar-nav">
                    <ul id="sidebarnav">
                        <SidebarSingleMenu
                            title="Dashboard"
                            icon="fas fa-home"
                            link={links.home}
                        />
                        {accountExpired &&
                            <SidebarSingleMenu
                                title="Acquista"
                                icon="fas fa-shopping-cart"
                                link={links.paymentPage} />
                            || <>
                                <SidebarSingleMenu
                                    title="Clienti"
                                    icon="fas fa-users"
                                    link={links.customers}
                                />
                                <SidebarMenu
                                    title="Apparecchiature"
                                    icon={<i><BoilerIcon color="#99abb4" width={20} additionalClass="me-2" /></i>}
                                    items={[
                                        { title: "Caldaie", link: links.caldaie },
                                        { title: "Bruciatori", link: links.bruciatori },
                                        { title: "Importa caldaie", link: links.caldaieImport },
                                    ]}
                                />
                                <SidebarSingleMenu
                                    title="Scadenze"
                                    icon="fas fa-exclamation-circle"
                                    link={links.expirations}
                                />
                                <SidebarMenu
                                    title="Interventi"
                                    icon="fas fa-truck"
                                    items={[
                                        { title: "Agenda Tecnici", link: links.interventiCalendar },
                                        { title: "Report Interventi", link: links.interventiReport },
                                    ]}
                                />
                                <SidebarSingleMenu
                                    title="Export Catasti"
                                    icon="fas fa-file-export"
                                    link={links.catastiExport}
                                />
                                <SidebarMenu
                                    title="Reports"
                                    icon="fas fa-chart-line"
                                    items={[
                                        { title: "Installazioni", link: links.brandsReport },
                                        { title: "Campagna sostituzione", link: links.plantsReplacementsReport },
                                    ]}
                                />
                                <SidebarSingleMenu
                                    title="Contratti"
                                    icon="fas fa-file-signature"
                                    link={links.tipiContratto}
                                />
                                <SidebarMenu
                                    title="Azienda"
                                    icon="fab fa-houzz"
                                    items={[
                                        { title: "La mia azienda", link: links.companyEdit },
                                        { title: "Tecnici", link: links.technicians },
                                    ]}
                                />
                                <SidebarSingleMenu
                                    title={<span>Aggiornamenti {news.length > 0 ? <span className="badge bg-danger text-white">NOVITA'!</span> : null}</span>}
                                    icon={`fas fa-star ${news.length > 0 ? "text-danger" : ""}`}
                                    link={links.news}
                                />
                                <SidebarMenu
                                    title="Impostazioni"
                                    icon="fas fa-cog"
                                    items={[
                                        { title: "Accounts azienda", link: links.companyOperators },
                                        { title: "Configurazione Scadenze", link: links.expirationsSettings },
                                    ]}
                                />
                            </>}
                    </ul>
                </nav>
            </div>
            <div className="sidebar-footer">
                <a href="" className="link" data-bs-toggle="tooltip" title="Settings">
                    <i className="fas fa-cogs" />
                </a>
                <a href="https://www.facebook.com/caldaiemanager" target="_blank" className="link" data-bs-toggle="tooltip" title="Facebook">
                    <i className="fab fa-facebook" />
                </a>
                <a href="https://www.instagram.com/caldaiemanager" className="link" data-bs-toggle="tooltip" title="instagram">
                    <i className="fab fa-instagram" />
                </a>
            </div>
        </aside>
    );
}

export default attach(canRequireUser(Sidebar), UIStore, (store, props) => {
    return { screenType: store.screenType }
});